import { useState, useCallback, useContext } from 'react';
import { CustomHeadersEditor } from '../../components/CustomHeadersEditor';
import { monitoringScopeApi } from '../../api/monitoring';
import { CrawlScopeConfig } from '../../components/CrawlScope';
import type { CrawlScopeUiState } from '../../components/CrawlScope';
import type { CrawlScopeConfig as ScopeConfig } from '../../types/crawlScope';
import type { ScopeType, SegmentFilter } from '../../api/types';
import { suggestedMonitoringName, type MonitoringIntent } from '../../utils/monitoringIntent';
import { Button, FormField, FormSection, FormModal, Input } from '../../components/ui';
import styles from './ProjectWizard.module.css';
import ContextHelp from '../../components/ContextHelp/ContextHelp';

import { logger } from '../../utils/logger';
import { ToastContext } from '../../hooks/useToast';

type ProjectWizardErrorKey = 'name' | 'sitemap_url' | 'specific_urls' | 'segment_filters';
type ProjectWizardErrors = Partial<Record<ProjectWizardErrorKey, string>>;

const VALIDATION_MESSAGES: Record<ProjectWizardErrorKey, string> = {
  name: 'Project name is required',
  sitemap_url: 'Validate a sitemap URL before creating the project.',
  specific_urls: 'Add at least one specific URL before creating the project.',
  segment_filters: 'Select at least one URL segment before creating the project.',
};

/** Step indices for the 2-step create flow. */
const BASICS_STEP = 0;
const ADVANCED_STEP = 1;

interface ProjectWizardProps {
  onComplete: (project: {
    name: string;
    url: string;
    sitemap_urls?: string[];
    custom_headers?: Record<string, string>;
    scope_type?: ScopeType;
    segment_filters?: SegmentFilter[];
    specific_urls?: string[];
    max_urls?: number;
    crawl_frequency?: 'daily' | 'weekly';
    onboarding_intent_id?: string;
  }) => void;
  onCancel: () => void;
  initialIntent?: MonitoringIntent | null;
}

/**
 * Monitoring project creation as a 2-step modal.
 *
 * Step 1 ("Basics & Scope") holds the project name and the whole
 * `CrawlScopeConfig`; step 2 ("Advanced") holds the two optional blocks
 * (User-Agent override and custom headers).
 *
 * The split deliberately keeps the name and the scope *together*: every
 * required field lives on step 1, so a single action can surface every
 * validation message at once instead of drip-feeding them one step at a time.
 *
 * Because step 2 is entirely optional, this wizard supplies its own footer
 * rather than using `FormModal`'s default stepped footer. The default footer
 * shows only "Next" until the last step, which would force users through an
 * optional screen just to reach the create action. Here "Create Project" is
 * present on both steps, alongside "Advanced options" on step 1.
 */
export default function ProjectWizard({ onComplete, onCancel, initialIntent }: ProjectWizardProps) {
  /**
   * Read the toast context directly (not the throwing `useToast()`) because
   * `ProjectWizard.validation.test.tsx` — frozen, not to be modified — renders
   * this component without a `ToastProvider`. In the real app `ToastProvider`
   * is always mounted at the root (`App.tsx`), so this is only ever missing
   * in that isolated test.
   */
  const toast = useContext(ToastContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [billingError, setBillingError] = useState(false);
  const [currentStep, setCurrentStep] = useState(BASICS_STEP);
  const [projectName, setProjectName] = useState(() => initialIntent ? suggestedMonitoringName(initialIntent.payload.page_url) : '');
  const [checkFrequency, setCheckFrequency] = useState<'daily' | 'weekly'>(() => initialIntent?.payload.frequency ?? 'daily');
  const [userAgent, setUserAgent] = useState('');
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>({});
  const [scopeConfig, setScopeConfig] = useState<ScopeConfig | null>(() => initialIntent ? {
    scope_type: 'specific_urls', sitemap_url: '', additional_sitemaps: [],
    segment_filters: [], specific_urls: [initialIntent.payload.page_url],
    include_patterns: [], exclude_patterns: [],
  } : null);
  /**
   * The scope step's transient UI state (validation result, discovered
   * segments, website-discovery results) lives here rather than inside
   * `CrawlScopeConfig`, because `FormModal` mounts only the current step: going
   * to "Advanced" and back remounts the scope component. Without this, the user
   * would come back to a blank, unvalidated form while `scopeConfig` still held
   * — and would still submit — their earlier input.
   */
  const [scopeUiState, setScopeUiState] = useState<CrawlScopeUiState | undefined>(undefined);
  const [formErrors, setFormErrors] = useState<ProjectWizardErrors>({});

  const clearFormError = useCallback((field: ProjectWizardErrorKey) => {
    setFormErrors((currentErrors) => {
      if (!currentErrors[field]) return currentErrors;
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  }, []);

  const handleScopeChange = useCallback((config: ScopeConfig) => {
    setScopeConfig(config);
    setFormErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors.sitemap_url;
      delete nextErrors.specific_urls;
      delete nextErrors.segment_filters;
      return nextErrors;
    });
  }, []);

  /** Every required field on step 1 (name + scope), as an error map. */
  const collectBasicsErrors = useCallback((): ProjectWizardErrors => {
    const errors: ProjectWizardErrors = {};
    const trimmedProjectName = projectName.trim();
    // A sitemap scope needs a sitemap; a URL-list scope needs URLs. Segments are
    // optional now — none simply means "don't narrow the sitemap" — so there is no
    // longer anything to require there.
    const isUrlListScope = scopeConfig?.scope_type === 'specific_urls';

    if (!trimmedProjectName) errors.name = VALIDATION_MESSAGES.name;
    if (!isUrlListScope && !scopeConfig?.sitemap_url?.trim()) {
      errors.sitemap_url = VALIDATION_MESSAGES.sitemap_url;
    }
    if (isUrlListScope && !scopeConfig?.specific_urls?.some((url) => url.trim())) {
      errors.specific_urls = VALIDATION_MESSAGES.specific_urls;
    }

    return errors;
  }, [projectName, scopeConfig]);

  /** Gate for leaving step 1 via "Advanced options". */
  const validateBasicsStep = useCallback((): boolean => {
    const errors = collectBasicsErrors();
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [collectBasicsErrors]);

  /**
   * The full check run on create, whichever step it is triggered from. Step 2
   * has no required fields today, so this currently equals the step-1 check —
   * it stays a separate function so that adding a required advanced field
   * later cannot be skipped by creating straight from step 1.
   */
  const validateForm = useCallback((): boolean => {
    const errors: ProjectWizardErrors = { ...collectBasicsErrors() };
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [collectBasicsErrors]);

  const handleStepValidate = useCallback(
    (step: number): boolean => (step === BASICS_STEP ? validateBasicsStep() : true),
    [validateBasicsStep],
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validateForm()) {
      // Every message this can produce renders on step 1, so surface the step
      // that owns the offending fields rather than failing silently. A toast
      // makes the failure unmistakable even if the sitemap validates around
      // the same moment and reflows the scope section underneath the click.
      setCurrentStep(BASICS_STEP);
      toast?.error('Project not created', 'Fix the highlighted fields below and try again.');
      return;
    }
    setSubmissionError('');
    setBillingError(false);
    setIsSubmitting(true);
    try {
      const payload: any = { name: projectName.trim(), crawl_frequency: checkFrequency };
      if (initialIntent) payload.onboarding_intent_id = initialIntent.id;

      if (scopeConfig?.sitemap_url) payload.url = scopeConfig.sitemap_url;

      // Merge user-agent into custom headers
      const mergedHeaders = { ...customHeaders };
      if (userAgent.trim()) mergedHeaders['User-Agent'] = userAgent.trim();
      if (Object.keys(mergedHeaders).length > 0) payload.custom_headers = mergedHeaders;

      // Scope configuration
      if (scopeConfig) {
        payload.scope_type = scopeConfig.scope_type;

        // Both are sent whenever present, regardless of scope_type: segments
        // narrow the sitemap, and specific_urls are ADDITIVE to whatever it
        // contributes. Gating them on scope_type is what made "the whole sitemap
        // plus these URLs" impossible to express.
        const enabled = scopeConfig.segment_filters.filter(f => f.enabled);
        if (enabled.length > 0) payload.segment_filters = enabled;

        const valid = scopeConfig.specific_urls.filter(u => u.trim());
        if (valid.length > 0) payload.specific_urls = valid;
        if (scopeConfig.max_urls) payload.max_urls = scopeConfig.max_urls;
      }

      await onComplete(payload);
    } catch (error) {
      logger.error('Failed to create project:', error);
      const apiError = error && typeof error === 'object'
        ? error as { code?: string; message?: string }
        : { message: 'Could not create the project. Please try again.' };
      const needsBilling = apiError.code === 'PAYMENT_REQUIRED' || apiError.code === 'INSUFFICIENT_CREDITS';
      setBillingError(needsBilling);
      setSubmissionError(needsBilling
        ? 'Your current plan cannot create this project. Review your billing details and try again.'
        : apiError.message || 'Could not create the project. Please try again.');
      setCurrentStep(BASICS_STEP);
    } finally {
      setIsSubmitting(false);
    }
  };

  const basicsStep = (
    <>
      <p className={styles.subtitle}>Track changes and get alerts for your website</p>
      {submissionError && (
        <p className={styles.submitError} role="alert">
          {submissionError}{' '}
          {billingError && <a href="/admin/billing/upgrade">Review billing</a>}
        </p>
      )}

      {/* ── Project Name ──────────────────────────────────────── */}
      <FormSection title="Basic Information">
        <FormField label="Project Name" htmlFor="monitoring-project-name" required error={formErrors.name}>
          <Input
            id="monitoring-project-name"
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
              clearFormError('name');
            }}
            placeholder="e.g., Company Website"
            disabled={isSubmitting}
            error={!!formErrors.name}
            fullWidth
            /* No native `required` here: it would make the browser cancel form
               submission on an empty field, so validateForm()'s friendlier
               per-field messages ("Project name is required", plus the sitemap
               /segment checks) would never run. FormField's `required` above
               still renders the visual asterisk. */
          />
        </FormField>
        <FormField label="Check Frequency" htmlFor="monitoring-check-frequency">
          <select
            id="monitoring-check-frequency"
            value={checkFrequency}
            onChange={(event) => setCheckFrequency(event.target.value as 'daily' | 'weekly')}
            disabled={isSubmitting}
            className={styles.frequencySelect}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </FormField>
        <ContextHelp label="How often should I check?">Daily checks suit pages that change frequently; weekly checks suit slower updates. For example, check a competitor’s pricing page daily. Each check can use credits.</ContextHelp>
      </FormSection>

      {/* ── Unified Crawl Scope Configuration ─────────────────── */}
      <FormSection title="Monitoring Scope">
        <CrawlScopeConfig
          api={monitoringScopeApi}
          initialConfig={scopeConfig ?? undefined}
          initialUiState={scopeUiState}
          onUiStateChange={setScopeUiState}
          onScopeChange={handleScopeChange}
          showWebsiteDiscovery={true}
          showScopeTypeSelector={true}
          showSpecificUrls={true}
          showMaxUrls={true}
          showRegexPatterns={true}
          showAdditionalSitemaps={false}
          showCostPreview={false}
          validationErrors={{
            sitemap_url: formErrors.sitemap_url,
            specific_urls: formErrors.specific_urls,
            segment_filters: formErrors.segment_filters,
          }}
          onValidationErrorClear={(field) => {
            if (field === 'sitemap_url' || field === 'specific_urls' || field === 'segment_filters') {
              clearFormError(field);
            }
          }}
          disabled={isSubmitting}
        />
      </FormSection>
    </>
  );

  const advancedStep = (
    <>
      {/* ── User Agent ────────────────────────────────────────── */}
      <FormSection title="User Agent (Optional)" description="Set a custom User-Agent string for crawl requests.">
        <FormField
          label="User-Agent String"
          htmlFor="monitoring-user-agent"
          helpText="Leave empty to use the default crawler User-Agent."
        >
          <Input
            id="monitoring-user-agent"
            value={userAgent}
            onChange={(e) => setUserAgent(e.target.value)}
            placeholder="e.g., Mozilla/5.0 (compatible; MyBot/1.0)"
            disabled={isSubmitting}
            fullWidth
          />
        </FormField>
      </FormSection>

      {/* ── Custom Headers ────────────────────────────────────── */}
      <FormSection title="Custom Headers (Optional)" description="Add custom headers to bypass bot detection or access restricted content.">
        <CustomHeadersEditor
          headers={customHeaders}
          onChange={setCustomHeaders}
          disabled={isSubmitting}
          showTemplates={true}
        />
      </FormSection>
    </>
  );

  const submitLabel = isSubmitting ? 'Creating...' : 'Create Project';

  return (
    <FormModal
      isOpen
      onClose={onCancel}
      title="Create Monitoring Project"
      size="large"
      steps={[
        { label: 'Basics & Scope', content: basicsStep },
        { label: 'Advanced', content: advancedStep },
      ]}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      onStepValidate={handleStepValidate}
      onSubmit={() => {
        void handleSubmit();
      }}
      submitLabel={submitLabel}
      isSubmitting={isSubmitting}
      footer={(formId) => (
        <div className={styles.stepFooter}>
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <div className={styles.stepFooterRight}>
            {currentStep === ADVANCED_STEP && (
              <Button
                variant="secondary"
                onClick={() => setCurrentStep(BASICS_STEP)}
                disabled={isSubmitting}
              >
                Back
              </Button>
            )}
            {currentStep === BASICS_STEP && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (handleStepValidate(BASICS_STEP)) setCurrentStep(ADVANCED_STEP);
                }}
                disabled={isSubmitting}
              >
                Advanced options
              </Button>
            )}
            <Button
              type="submit"
              form={formId}
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      )}
    >
      {/* Body is supplied by `steps`; FormModal ignores children when stepped. */}
      {null}
    </FormModal>
  );
}
